import { useTranslation } from 'react-i18next';
import { MemberPortalPreview } from './MemberPortalPreview';
import type { MemberCenterAppearanceV1 } from '@pokerroom/types';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  MemberCardTemplate,
  MemberCardTemplateRegistry,
  MemberLevelDto,
} from '@pokerroom/types';
import { api, apiClient } from '../../lib/api';
type Edit = Pick<MemberCardTemplate, 'name' | 'showLevelName' | 'showPhone' | 'showTimedPoints'>;
const defaults: Edit = {
  name: 'ZIPA會員卡',
  showLevelName: true,
  showPhone: false,
  showTimedPoints: true,
};
function CardImage({
  templateId,
  levelId,
  assetId,
  disabled,
  onUpload,
}: {
  templateId: string;
  levelId: string;
  assetId?: string;
  disabled: boolean;
  onUpload: (file: File) => void;
}) {
  const { t } = useTranslation('member');
  const path = `/members/card-templates/${templateId}/images/${levelId}`;
  const [preview, setPreview] = useState('');
  const [extension, setExtension] = useState('.png');
  const [message, setMessage] = useState('');
  useEffect(() => {
    setPreview('');
    if (!assetId) return;
    const abort = new AbortController();
    let url = '';
    void apiClient
      .get<Blob>(path, { responseType: 'blob', signal: abort.signal })
      .then((r) => {
        if (abort.signal.aborted) return;
        setExtension(r.headers['content-type'] === 'image/jpeg' ? '.jpg' : '.png');
        url = URL.createObjectURL(r.data);
        setPreview(url);
      })
      .catch(() => {
        if (!abort.signal.aborted) setMessage(t('portalEditor.imageError'));
      });
    return () => {
      abort.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [assetId, path, t]);
  return (
    <div>
      {preview && (
        <img src={preview} alt={t('portalEditor.imageAlt')} className="w-40 rounded my-2" />
      )}
      <label className="card-template-upload">
        <span>{t('portalEditor.upload')}</span>
        <input
          aria-label={t('portalEditor.uploadLabel')}
          type="file"
          accept="image/jpeg,image/png"
          disabled={disabled}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) {
              if (file.size > 5_000_000) setMessage(t('portalEditor.imageLimit'));
              else {
                setMessage('');
                onUpload(file);
              }
            }
          }}
        />
      </label>
      {preview && (
        <a href={preview} download={`member-card-${levelId}${extension}`}>
          {t('portalEditor.download')}
        </a>
      )}
      {message && <p role="status">{message}</p>}
    </div>
  );
}
export function MemberCardTemplateEditor({
  venueId,
  appearance,
}: {
  venueId: string;
  appearance: MemberCenterAppearanceV1;
}) {
  const { t } = useTranslation('member');
  const queryClient = useQueryClient(),
    key = ['member-card-templates', venueId];
  const { data, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: () => api.get<MemberCardTemplateRegistry>('/members/card-templates'),
    retry: false,
  });
  const { data: levels = [] } = useQuery({
    queryKey: ['member-levels', venueId],
    queryFn: () => api.get<MemberLevelDto[]>('/members/levels'),
  });
  const [selected, setSelected] = useState<string>();
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (data && !initialized) {
      setSelected(data.activeTemplateId ?? undefined);
      setInitialized(true);
    }
  }, [data, initialized]);
  const [draft, setDraft] = useState<Edit>(defaults);
  const [busy, setBusy] = useState(false),
    [message, setMessage] = useState('');
  const template = data?.templates.find((t) => t.id === selected);
  useEffect(() => {
    if (template)
      setDraft({
        name: template.name,
        showLevelName: template.showLevelName,
        showPhone: template.showPhone,
        showTimedPoints: template.showTimedPoints,
      });
  }, [template]);
  async function change(
    path: string,
    body: unknown,
    method: 'post' | 'put' = 'post',
    multipart = false,
  ) {
    if (!data || busy) return;
    setBusy(true);
    setMessage('');
    try {
      const saved = await api[method]<MemberCardTemplateRegistry>(
        path,
        body,
        multipart ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined,
      );
      queryClient.setQueryData(key, saved);
      if (path === '/members/card-templates') setSelected(saved.templates.at(-1)?.id);
      setMessage(t('portalEditor.templateSaved'));
    } catch {
      setMessage(t('portalEditor.templateError'));
    } finally {
      setBusy(false);
    }
  }
  if (isLoading) return <p>{t('portalEditor.templateLoading')}</p>;
  if (isError || !data) return <p role="alert">{t('portalEditor.templateLoadError')}</p>;
  return (
    <section
      className="member-card-template-editor my-6 border rounded-xl p-4"
      aria-label={t('portalEditor.templateSettings')}
    >
      <div className="member-template-grid">
        <div className="member-template-controls">
          <h2>{t('portalEditor.templates')}</h2>
          <p>{t('portalEditor.templateHint')}</p>
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>{t('portalEditor.status')}</th>
                  <th>{t('portalEditor.name')}</th>
                  <th>{t('portalEditor.created')}</th>
                  <th>{t('portalEditor.updated')}</th>
                  <th>{t('portalEditor.creator')}</th>
                  <th>{t('portalEditor.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.templates.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.id === data.activeTemplateId
                        ? t('portalEditor.active')
                        : t('portalEditor.inactive')}
                    </td>
                    <td>{item.name}</td>
                    <td>{new Date(item.createdAt).toLocaleString()}</td>
                    <td>{new Date(item.updatedAt).toLocaleString()}</td>
                    <td>{item.createdBy}</td>
                    <td>
                      <button disabled={busy} onClick={() => setSelected(item.id)}>
                        {t('portalEditor.edit')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            className="btn btn-sm my-2"
            disabled={busy}
            onClick={() => {
              setSelected(undefined);
              setDraft(defaults);
            }}
          >
            {t('portalEditor.new')}
          </button>
          <div className="flex flex-col gap-3">
            <label>
              {t('portalEditor.name')}
              <input
                className="input"
                value={draft.name}
                maxLength={64}
                disabled={busy}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </label>
            {(
              [
                ['showLevelName', t('portalEditor.showLevelName')],
                ['showPhone', t('portalEditor.showPhone')],
                ['showTimedPoints', t('portalEditor.showTimedPoints')],
              ] as const
            ).map(([field, label]) => (
              <label key={field}>
                <input
                  type="checkbox"
                  checked={draft[field]}
                  disabled={busy}
                  onChange={(e) => setDraft({ ...draft, [field]: e.target.checked })}
                />{' '}
                {label}
              </label>
            ))}
            <button
              className="btn btn-sm"
              disabled={busy}
              onClick={() =>
                void change(
                  template ? `/members/card-templates/${template.id}` : '/members/card-templates',
                  { template: draft, revision: data.revision },
                  template ? 'put' : 'post',
                )
              }
            >
              {template ? t('portalEditor.saveTemplate') : t('portalEditor.createTemplate')}
            </button>
            {template && (
              <>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={busy || template.id === data.activeTemplateId}
                  onClick={() =>
                    void change(`/members/card-templates/${template.id}/activate`, {
                      revision: data.revision,
                    })
                  }
                >
                  {t('portalEditor.activate')}
                </button>
                <p>{t('portalEditor.imageHint')}</p>
                {levels.map((level) => (
                  <div key={level.id}>
                    <strong>{level.displayName}</strong>
                    <CardImage
                      templateId={template.id}
                      levelId={level.id}
                      assetId={template.images[level.id]}
                      disabled={busy}
                      onUpload={(file) => {
                        const form = new FormData();
                        form.append('file', file);
                        form.append('revision', data.revision ?? 'null');
                        void change(
                          `/members/card-templates/${template.id}/images/${level.id}`,
                          form,
                          'post',
                          true,
                        );
                      }}
                    />
                  </div>
                ))}
              </>
            )}
          </div>
          {message && <p role="status">{message}</p>}
        </div>
        <MemberPortalPreview
          appearance={appearance}
          card={draft}
          template={template}
          levels={levels}
        />
      </div>
    </section>
  );
}
